import {useAuthUserId, useAuthClient} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import React, {useEffect, useImperativeHandle, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import {useCommentMarkAllRead} from 'services/comment/comment.hooks';
import TimelineList from 'components/organisms/Lists/TimelineList/TimelineList';
import {useClientTimelineList, useTimelineMarkAllRead} from 'services/client/timeline/timeline.hooks';
import StyledClientNotificationList
  from 'components/organisms/Lists/ClientNotificationList/ClientNotificationList.styles';
import {usePopper} from 'components/molecules/Poppers/ContextPopper/ContextPopper';

const ClientNotificationList = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'ClientNotificationList');

  const innerRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const popper = usePopper();

  const client = useAuthClient();
  const userId = useAuthUserId();

  const clientCommentAllRead = useCommentMarkAllRead();
  const clientTimelineAllRead = useTimelineMarkAllRead();

  const timeline = useClientTimelineList({
    clientId: client?.clientId,
    page: 0,
    pageSize: 15,
    filter: [
      {id: 'notification', value: false},
      {id: 'actionList', value: true},
      {id: 'excludedTypes', value: ['additions', 'status', 'csi', 'deal_leader']},
    ]
  }, {
    ...constants.queryOptions.infinite,
    enabled: utils.isDefined(client?.clientId)
  });
  const isLoading = timeline.status?.isLoading || !utils.isDefined(timeline.data);

  useEffect(() => {
    const scroll = () => {
      return innerRef.current?.refs?.ref?.current?.querySelector('.DataList-list')?.scrollTo({top: 0, left: 0});
    }

    return utils.observeRetry(scroll, 5);
  }, [client?.clientId]);

  const isItemEqual = (item, selected) => {
    return item.timelineId === selected.timelineId;
  }

  const handleEdit = (e, item, editing) => {
    if (item?.timelineType === 'task') {
      popper?.close?.();
    } else {
      popper?.clickAway(!editing);
      e?.stopPropagation();
    }
  }

  const handleVisibilityChange = (items) => {
    const unreadComments = items?.filter?.((ti) => ti.recordType === 'comment' && ti.isRead === false)
    if (unreadComments?.length > 0) {
      clientCommentAllRead.mutation.mutate({
        clientId: client?.clientId,
        group: true,
        commentIds: unreadComments.map((d) => d.commentId)
      });
    }
    const unreadTasks = items?.filter?.((ti) => ti.relationType === 'task' && +ti.userId === +userId && ti.isRead === false)
    if (unreadTasks?.length > 0) {
      clientTimelineAllRead.mutation.mutate({
        clientId: client?.clientId,
        taskIds: unreadTasks.map((d) => d.relationId)
      });
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledClientNotificationList ref={innerRef} {...innerProps}>
    <TimelineList count={timeline?.meta?.resultsCount}
                  timeline={timeline.data}
                  loaderCount={4}
                  dataKey="timelineId"
                  emptyText="No notifications found"
                  isItemEqual={isItemEqual}
                  onEdit={handleEdit}
                  onFetchMore={timeline.query?.fetchNextPage}
                  onVisibilityChange={handleVisibilityChange}
                  isLoading={isLoading}
                  showProgressBar={timeline.status?.isLoadingNext}/>
  </StyledClientNotificationList>
});

ClientNotificationList.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default ClientNotificationList;
